import type { TaskListType } from "@orsane/schema/task-lists";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "@/components/QueryProvider";
import { useTRPC } from "@/lib/trpc";
import { useTaskListsQueryOptions } from "@/queries/list-queries";
import type { TaskListPreferences } from "@/types/list";

export const useInsertTaskListMutation = () => {
    const trpc = useTRPC();
    const xx = useTaskListsQueryOptions();
    const myMutationOptions = trpc.taskList.insert.mutationOptions({
        onSuccess: (data, _vars, _on, context) => {
            context.client.setQueryData(xx.queryKey, (oldData: any) => [...oldData!, data]);
        },
    });
    return useMutation(myMutationOptions);
};

export const useDeleteTaskListMutation = () => {
    const trpc = useTRPC();
    const mutationOptions = trpc.taskList.delete.mutationOptions({
        onSuccess: async (_, _vars, _on, context) => {
            const key = trpc.taskList.list.queryOptions().queryKey;
            await context.client.invalidateQueries({ queryKey: key });
        },
    });

    return useMutation(mutationOptions);
};

export const useArchiveTaskListMutation = () => {
    const trpcHook = useTRPC();

    const mutationOptions = trpcHook.taskList.archive.mutationOptions({
        onSuccess: async (_, { id }, _on, context) => {
            // invalidate list view (sidebar for example)
            context.client.invalidateQueries({ queryKey: trpc.taskList.list.queryOptions().queryKey });
            context.client.invalidateQueries({ queryKey: trpc.taskList.get.queryOptions({ id: id }).queryKey });
        },
    });

    return useMutation(mutationOptions);
};

/**
 * Leave a list you are a member of but did not create. Archiving and deleting
 * are reserved for the creator, so this is the only way for a member to get a
 * shared list out of their sidebar.
 */
export const useLeaveTaskListMutation = () => {
    const trpc = useTRPC();

    const mutationOptions = trpc.taskList.leave.mutationOptions({
        onSuccess: async (_, _vars, _on, context) => {
            await context.client.invalidateQueries({ queryKey: trpc.taskList.list.queryOptions().queryKey });
            // The "you were added to this list" notifications are removed server-side.
            context.client.invalidateQueries({ queryKey: trpc.notification.pathKey() });
        },
    });

    return useMutation(mutationOptions);
};

export const useUpdateTaskListColorMutation = () => {
    const trpc = useTRPC();
    const mutationOptions = trpc.taskList.updateColor.mutationOptions({
        onSuccess: async (_, _vars, _on, context) => {
            // invalidate everything tasklists
            context.client.invalidateQueries({ queryKey: trpc.taskList.pathKey() });
            context.client.invalidateQueries({ queryKey: trpc.taskList.list.pathKey() });
        },
    });
    return useMutation(mutationOptions);
};

export const useUpdateTaskListNameMutation = () => {
    const trpc = useTRPC();

    const myMutationOptions = trpc.taskList.updateName.mutationOptions({
        onSuccess: async (_, _vars, _on, context) => {
            context.client.invalidateQueries({ queryKey: trpc.taskList.pathKey() });
            context.client.invalidateQueries({ queryKey: trpc.taskList.list.pathKey() });
        },
    });

    return useMutation(myMutationOptions);
};

export const useUpdateTaskListPreferencesMutation = () => {
    const trpc = useTRPC();

    const opts = trpc.taskList.updatePreferences.mutationOptions({
        onMutate: async (updateData: { taskListId?: number; parentTaskId?: number; type: TaskListType; preferences: Record<string, any> }, context) => {
            const key = trpc.taskList.getPreferences.queryKey({
                taskListId: updateData.taskListId,
                parentTaskId: updateData.parentTaskId,
                type: updateData.type,
            });
            await context.client.cancelQueries({ queryKey: key });

            const previousPreferences = context.client.getQueryData<TaskListPreferences>(key);
            if (previousPreferences) {
                context.client.setQueryData(key, updateData.preferences);
            }
        },
    });

    return useMutation(opts);
};

export const updateTaskListOrderMutation = () => {
    const trpc = useTRPC();

    // we don't need to invalidate anything since we already persisted the order in local state (TaskListSection)
    const opts = trpc.taskList.updateOrder.mutationOptions();

    return useMutation(opts);
};

export const useAddTaskListUserMutation = () => {
    const trpc = useTRPC();

    return useMutation(
        trpc.taskList.addUser.mutationOptions({
            onSuccess: (_, input, _on, context) => {
                context.client.invalidateQueries({
                    queryKey: trpc.taskList.users.queryKey({ id: input.id }),
                });
            },
        })
    );
};

export const useRemoveTaskListUserMutation = () => {
    const trpc = useTRPC();
    return useMutation(
        trpc.taskList.removeUser.mutationOptions({
            onSuccess: (_, input, _on, context) => {
                context.client.invalidateQueries({
                    queryKey: trpc.taskList.users.queryKey({ id: input.id }),
                });
                // also invalidate the task list list because you might remove your own from a list
                context.client.invalidateQueries({
                    queryKey: trpc.taskList.list.queryKey(),
                });
            },
        })
    );
};
